import { useState, useCallback, useEffect } from 'react';
import api from '../services/api';
import { useLiveReload } from './common/useLiveReload';
import type { CitationVerdict, ProvenanceResponse, ProvenanceRow } from '../types';

interface ProvenancePanelProps {
  pageId: number;
  versionId?: number | null;
}

const VERDICT_CONFIG: Record<
  string,
  { label: string; color: string; bg: string }
> = {
  SUPPORTED: {
    label: 'Supported',
    color: 'text-green-700 dark:text-green-300',
    bg: 'bg-green-100 dark:bg-green-900/40',
  },
  NOT_SUPPORTED: {
    label: 'Not supported',
    color: 'text-red-700 dark:text-red-300',
    bg: 'bg-red-100 dark:bg-red-900/40',
  },
  UNRESOLVED: {
    label: 'Invented citation',
    color: 'text-red-700 dark:text-red-300',
    bg: 'bg-red-100 dark:bg-red-900/40',
  },
  UNCHECKED: {
    label: 'Unchecked',
    color: 'text-gray-500 dark:text-gray-400',
    bg: 'bg-gray-100 dark:bg-gray-700',
  },
};

function verdictKey(v: CitationVerdict): string {
  return v ?? 'UNCHECKED';
}

function VerdictBadge({ verdict }: { verdict: CitationVerdict }) {
  const cfg = VERDICT_CONFIG[verdictKey(verdict)] ?? VERDICT_CONFIG.UNCHECKED;
  return (
    <span
      className={`inline-block whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium ${cfg.color} ${cfg.bg}`}
    >
      {cfg.label}
    </span>
  );
}

// Strip a trailing kramdown/pandoc {#anchor} — it's a heading id for the ToC,
// not part of the heading text, so it shouldn't show in the section label.
function cleanHeading(s?: string | null): string {
  return (s || '').replace(/\s*\{#[^}]*\}\s*$/, '').trim();
}

function ProvenanceRowItem({ row }: { row: ProvenanceRow }) {
  const bad = row.verdict === 'NOT_SUPPORTED' || row.verdict === 'UNRESOLVED';
  const section = cleanHeading(row.section);
  return (
    <tr
      className={`border-b border-gray-100 align-top dark:border-gray-700 ${
        bad ? 'bg-red-50/50 dark:bg-red-900/10' : ''
      }`}
    >
      <td className="py-2 pr-3">
        <div className="break-words text-sm text-gray-900 dark:text-gray-100">
          “{row.claim}”
        </div>
        {section && (
          <div className="mt-0.5 break-words text-xs text-gray-400 dark:text-gray-500">
            {section}
          </div>
        )}
        {bad && row.issue && (
          <div className="mt-1 break-words text-xs text-red-600 dark:text-red-400">
            {row.issue}
          </div>
        )}
      </td>
      <td className="py-2 pr-3">
        {row.point ? (
          <div className="break-words text-sm text-gray-700 dark:text-gray-300">
            {row.point}
          </div>
        ) : (
          <div className="text-xs italic text-gray-400 dark:text-gray-500">
            no source fact (id was never issued)
          </div>
        )}
        {row.source_url && (
          <a
            href={row.source_url}
            target="_blank"
            rel="noreferrer"
            title={row.source_url}
            className="mt-0.5 block truncate text-xs text-blue-600 hover:underline dark:text-blue-400"
          >
            {row.source_title || row.source_url}
          </a>
        )}
      </td>
      <td className="py-2">
        <VerdictBadge verdict={row.verdict ?? null} />
      </td>
    </tr>
  );
}

export default function ProvenancePanel({ pageId, versionId }: ProvenancePanelProps) {
  const [data, setData] = useState<ProvenanceResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!versionId) return;
    setLoading(true);
    setError(null);
    try {
      setData(await api.getVersionProvenance(pageId, versionId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load provenance');
    } finally {
      setLoading(false);
    }
  }, [pageId, versionId]);

  useEffect(() => {
    load();
  }, [load]);
  // GROUND CAN RE-CHECK THE SAME VERSION: a run that revises nothing writes its
  // verdicts onto the version already open, so the version id does not move
  // and the claims table kept the old verdicts beside new counts.
  useLiveReload(
    (e) => e?.event_type === 'page_build' && e.page_id === pageId && ['ground_completed', 'completed'].includes(e.status),
    () => void load(),
  );

  if (!versionId) {
    return (
      <div className="rounded-lg border border-gray-200 p-4 text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
        Build the page to see citation provenance.
      </div>
    );
  }

  const summary = data?.summary;
  const flagged = summary ? summary.NOT_SUPPORTED + summary.UNRESOLVED : 0;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Citation provenance
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Every cited claim, its source, and whether the source supports it.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {error && <div className="text-sm text-red-600 dark:text-red-400">{error}</div>}

      {data && !data.has_structured_content && (
        <div className="text-sm text-gray-500 dark:text-gray-400">
          This version has no structured citations (built before the citation
          pipeline, or has no cited claims).
        </div>
      )}

      {summary && data?.has_structured_content && (
        <>
          <div className="mb-3 flex flex-wrap gap-2 text-xs">
            <span className="rounded bg-gray-100 px-2 py-0.5 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
              {summary.total} cited
            </span>
            <span className="rounded bg-green-100 px-2 py-0.5 text-green-700 dark:bg-green-900/40 dark:text-green-300">
              {summary.SUPPORTED} supported
            </span>
            {flagged > 0 && (
              <span className="rounded bg-red-100 px-2 py-0.5 font-medium text-red-700 dark:bg-red-900/40 dark:text-red-300">
                {flagged} flagged
              </span>
            )}
          </div>

          {data.rows.length === 0 ? (
            <div className="text-sm text-gray-500 dark:text-gray-400">
              No cited claims in this version.
            </div>
          ) : (
            <div className="overflow-x-auto">
              {/* table-fixed + explicit widths: without it the long, unbroken
                  source URL forces its column wide and pushes the row off-screen,
                  squeezing the claim column to a sliver. Fixed widths let both
                  text columns wrap instead. */}
              <table className="w-full table-fixed border-collapse text-left">
                <colgroup>
                  <col className="w-[42%]" />
                  <col className="w-[42%]" />
                  <col className="w-[16%]" />
                </colgroup>
                <thead>
                  <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-400 dark:border-gray-600 dark:text-gray-500">
                    <th className="py-1.5 pr-3 font-medium">Claim</th>
                    <th className="py-1.5 pr-3 font-medium">Source fact</th>
                    <th className="py-1.5 font-medium">Verdict</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row, i) => (
                    <ProvenanceRowItem key={`${row.fact_id}-${i}`} row={row} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
